import { Navigate, Outlet, useParams } from 'react-router-dom'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { kMyDriveName, matterPaths } from '@/routing/paths'

type FilesExperience = 'legacy' | 'new'

/**
 * Route guard that keeps the files routes consistent with the active experience for the current user
 * (mirrors the BrandAccessGuard pattern: read auth-derived state, render <Outlet /> or redirect):
 * - new files UX on  → legacy `/files/...` routes redirect to the new `/user-files` root
 * - new files UX off → new `/user-files/...` routes redirect to the legacy drive (also covers Haiku)
 *
 * Folder/deep-link position isn't preserved across the redirect (legacy folder paths vs new folder
 * IDs don't map), so we land on the root. Guards direct navigation / bookmarks; in-app entry points
 * are already gated by the flag.
 */
export function FilesExperienceRedirect({ experience }: { experience: FilesExperience }) {
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)
  // matterId comes from the `m/:matterId` parent segment, so it's effectively always present here.
  // The `matterId &&` checks are just a safety net; without one we fall through to <Outlet />.
  const { matterId } = useParams<{ matterId: string }>()

  if (matterId && experience === 'legacy' && newFilesUxEnabled) {
    return <Navigate to={matterPaths.userFiles.root(matterId)} replace />
  }
  if (matterId && experience === 'new' && !newFilesUxEnabled) {
    return <Navigate to={matterPaths.files.drive(matterId, kMyDriveName)} replace />
  }

  return <Outlet />
}
